<template>
  <v-container fluid class="pa-4">
    <!-- ===================== -->
    <!-- Portfolio Verlauf -->
    <!-- ===================== -->
    <v-row class="mb-4" align="center">
      <v-col class="portfolio-title-row">
        <h2>Portfolio - Verlauf</h2>
        <v-radio-group
          v-model="periodMode"
          aria-label="Ansicht"
          inline
          density="compact"
          hide-details
          class="portfolio-period-mode"
        >
          <v-radio
            v-for="option in periodModeOptions"
            :key="option.value"
            :label="option.title"
            :value="option.value"
            density="compact"
          />
        </v-radio-group>
        <v-btn color="primary" :loading="loading" @click="loadAll(true)">
          Neu laden
        </v-btn>
        <v-switch
          v-model="showPositions"
          label="Positionen anzeigen"
          density="compact"
          hide-details
          class="portfolio-title-switch"
        />
      </v-col>
    </v-row>

    <v-row class="mb-4 portfolio-filter-row">
      <v-col v-if="!showDaily" cols="12" md="2">
        <v-select
          v-model="fromYear"
          :items="yearOptions"
          label="Von Jahr"
          density="compact"
        />
      </v-col>

      <v-col v-if="!showDaily" cols="12" md="2">
        <v-select
          v-model="toYear"
          :items="yearOptions"
          label="Bis Jahr"
          density="compact"
        />
      </v-col>

      <v-col v-if="showDaily" cols="12" md="2">
        <v-text-field
          v-model="dailyFrom"
          type="date"
          label="Von Tag"
          density="compact"
          hide-details
        />
      </v-col>

      <v-col v-if="showDaily" cols="12" md="2">
        <v-text-field
          v-model="dailyTo"
          type="date"
          label="Bis Tag"
          :max="currentDateIso"
          density="compact"
          hide-details
        />
      </v-col>

      <v-col cols="12" md="8" class="portfolio-switches">
        <v-switch
          v-model="showProjection"
          label="Hochrechnung"
          density="compact"
          hide-details
          class="portfolio-switch-control"
        />
        <v-select
          v-model="projectionPeriods"
          :items="projectionPeriodOptions"
          label="Perioden"
          density="compact"
          hide-details
          class="portfolio-period-select"
        />
        <v-text-field
          v-model="projectionTargetValue"
          label="Zielwert"
          density="compact"
          hide-details
          class="portfolio-target-input"
        />
      </v-col>
    </v-row>

<v-row class="mb-4">
  <v-col cols="12" md="3">
    <v-select
      v-model="selectedTypes"
      :items="stockTypeOptions"
      label="Stock Typ"
      multiple
      chips
      closable-chips
      density="compact"
      hide-details
    />

    <template v-if="isAdmin">
      <v-select
        v-model="selectedUserIds"
        :items="userOptions"
        item-title="title"
        item-value="value"
        label="User"
        multiple
        chips
        closable-chips
        density="compact"
        hide-details
        class="mt-3"
      />

      <div class="portfolio-user-actions">
        <v-btn size="small" variant="text" @click="selectDefaultUsers">{{ defaultUserGroupLabel }}</v-btn>
        <v-btn size="small" variant="text" @click="selectAllUsers">Alle</v-btn>
        <v-btn size="small" variant="text" @click="clearUsers">Keine</v-btn>
        <v-btn size="small" variant="text" @click="loadAvailableUsers">User laden</v-btn>
      </div>

      <v-alert v-if="availableUsers.length === 0" type="info" variant="tonal" class="mt-2">
        Keine User geladen.
      </v-alert>
    </template>
  </v-col>

  <v-col cols="12" md="5">
    <v-card variant="tonal" class="portfolio-mini-chart-card">
      <div class="text-caption mb-2 d-flex flex-wrap justify-space-between ga-2">
        <span>Wert Gesamtverlauf</span>
        <span v-if="portfolioTimelineMaximum" class="portfolio-maximum-label">
          Max: {{ formatEUR0(portfolioTimelineMaximum.wert_gesamt) }}
          · {{ formatDateDE(portfolioTimelineMaximum.stichtag) }}
        </span>
      </div>
      <div class="portfolio-mini-chart-wrap">
        <canvas ref="portfolioTimelineCanvas"></canvas>
      </div>
    </v-card>
  </v-col>

  <v-col cols="12" md="4">
    <v-card variant="tonal" class="portfolio-stat-card">
      <div class="text-caption mb-2">Durchschnitt Delta</div>
      <div class="text-h6 font-weight-bold d-flex flex-wrap align-baseline ga-2">
        <span :class="diffClass(averagePeriodDelta)">
          {{ averagePeriodDelta === null ? "-" : formatEUR(averagePeriodDelta) }}
        </span>
        <span :class="diffClass(averagePeriodDeltaPct)">
          ({{ averagePeriodDeltaPct === null ? "-" : formatPct(averagePeriodDeltaPct) }})
        </span>
      </div>
      <div class="text-caption mt-1">
        pro {{ periodUnitLabel }}
      </div>
      <div class="text-caption mt-3 text-medium-emphasis">
        {{ averageDeltaCount }} Deltas in der aktuellen Tabelle
      </div>
      <div v-if="showProjection && projectionSummary" class="text-caption mt-2 portfolio-projection-text">
        Nach {{ projectionSummary.periods }} {{ projectionSummary.periodLabel }} ist der prognostizierte Gesamtwert
        {{ formatEUR(projectionSummary.totalValue) }}
        <template v-if="projectionSummary.targetText">
          <br>
          {{ projectionSummary.targetText }}
        </template>
      </div>
    </v-card>
  </v-col>
</v-row>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
      {{ error }}
    </v-alert>

    <v-data-table
      v-model:expanded="expandedRows"
      class="portfolio-sticky-table"
      :headers="headers"
      :items="rows"
      :items-per-page="-1"
      fixed-header
      item-key="periodKey"
      item-value="periodKey"
      :show-expand="showPositions"
      :loading="loading"
    >
      <template #item.stichtag="{ item }">
        {{ formatDateDE(item.stichtag) }}
      </template>

      <template #item.wert_gesamt="{ item }">
        <strong>{{ formatEUR(item.wert_gesamt) }}</strong>
      </template>

      <template #item.diff="{ item }">
        <span :class="diffClass(item.diff)">
          {{ item.diff === null ? "-" : formatEUR0(item.diff) }}
        </span>
      </template>

      <template #item.yoy="{ item }">
        <span :class="diffClass(item.yoy)">
          {{ item.yoy === null ? "-" : formatPct(item.yoy) }}
        </span>
      </template>

<template #item.diff_aktie="{ item }">
  <span :class="diffClass(item.diff_aktie)">
    {{ item.diff_aktie === null ? "-" : formatEUR0(item.diff_aktie) }}
  </span>
</template>

<template #item.diff_etf="{ item }">
  <span :class="diffClass(item.diff_etf)">
    {{ item.diff_etf === null ? "-" : formatEUR0(item.diff_etf) }}
  </span>
</template>

<template #item.diff_rohstoff="{ item }">
  <span :class="diffClass(item.diff_rohstoff)">
    {{ item.diff_rohstoff === null ? "-" : formatEUR0(item.diff_rohstoff) }}
  </span>
</template>

<template #item.diff_cash="{ item }">
  <span :class="diffClass(item.diff_cash)">
    {{ item.diff_cash === null ? "-" : formatEUR0(item.diff_cash) }}
  </span>
</template>

    <template v-if="showPositions" #expanded-row="{ item, columns }">
      <tr>
        <td :colspan="columns.length">
          <div class="pa-4">
            <v-row class="mb-3" v-if="mdAndUp">
              <v-col cols="12" md="3">
                <v-card variant="tonal" class="pa-3">
                  <div class="text-caption">Delta {{ comparisonPeriodLabel }} Aktie</div>
                  <div :class="diffClass(item.diff_aktie)">
                    {{ item.diff_aktie === null ? "-" : formatEUR(item.diff_aktie) }}
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="3">
                <v-card variant="tonal" class="pa-3">
                  <div class="text-caption">Delta {{ comparisonPeriodLabel }} ETF</div>
                  <div :class="diffClass(item.diff_etf)">
                    {{ item.diff_etf === null ? "-" : formatEUR(item.diff_etf) }}
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="3">
                <v-card variant="tonal" class="pa-3">
                  <div class="text-caption">Delta {{ comparisonPeriodLabel }} Rohstoff</div>
                  <div :class="diffClass(item.diff_rohstoff)">
                    {{ item.diff_rohstoff === null ? "-" : formatEUR(item.diff_rohstoff) }}
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="3">
                <v-card variant="tonal" class="pa-3">
                  <div class="text-caption">Delta {{ comparisonPeriodLabel }} Cash</div>
                  <div :class="diffClass(item.diff_cash)">
                    {{ item.diff_cash === null ? "-" : formatEUR(item.diff_cash) }}
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <div v-if="showPositions">
              <v-alert
                v-if="getRowPositionState(item).error"
                type="error"
                variant="tonal"
                class="mb-3"
              >
                {{ getRowPositionState(item).error }}
              </v-alert>

              <v-data-table
                class="portfolio-sticky-table portfolio-sticky-table--nested"
                :headers="posHeaders"
                :items="getRowPositions(item)"
                :items-per-page="-1"
                density="compact"
                fixed-header
                height="320"
                :loading="getRowPositionState(item).loading"
              >
                <template #item.wert="{ item: p }">
                  {{ formatEUR(p.wert) }}
                </template>
              </v-data-table>
            </div>
          </div>
        </td>
      </tr>
    </template>
    </v-data-table>


    <!-- ===================== -->
<!-- NEU: Portfolio High-/Low-Scores -->
    <!-- ===================== -->
    <v-card class="mt-6" variant="outlined">
      <v-card-title class="d-flex align-center justify-space-between portfolio-score-title">
        <div class="portfolio-score-heading">
          <div class="text-h6">Portfolio High-/Low-Scores</div>
          <div class="text-caption">
            Quelle: <code>/portfolio/snapshots/best-gains</code> und <code>/portfolio/snapshots/worst-losses</code>
          </div>
        </div>

        <div class="portfolio-score-actions">
          <v-select
            v-model="snapshotScoreValueMode"
            :items="snapshotScoreValueModeOptions"
            item-title="title"
            item-value="value"
            label="Wert"
            density="compact"
            hide-details
            class="portfolio-score-value-mode"
          />
          <v-select
            v-model="snapshotScoreBasis"
            :items="snapshotScoreBasisOptions"
            item-title="title"
            item-value="value"
            label="Basis"
            density="compact"
            hide-details
            class="portfolio-score-basis"
          />
          <v-btn
            color="primary"
            variant="tonal"
            :loading="snapshotScoresLoading"
            class="portfolio-score-load-btn"
            @click="loadSnapshotScores(true)"
          >
            Scores laden
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <div class="text-caption mb-3">
          Zeitraum: <strong>{{ snapshotScoreFrom }}</strong> -> <strong>{{ snapshotScoreTo }}</strong>,
          je Feld <strong>1 Treffer</strong>
        </div>

        <v-alert v-if="snapshotScoresError" type="error" variant="tonal" class="mb-4">
          {{ snapshotScoresError }}
        </v-alert>

        <v-table density="compact" class="portfolio-score-table">
          <thead>
            <tr>
              <th>Zeitraum</th>
              <template v-for="period in snapshotScorePeriods" :key="period.value">
                <th class="text-right">{{ period.title }} High</th>
                <th class="text-right">{{ period.title }} Low</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-if="snapshotScoresLoading">
              <td :colspan="1 + snapshotScorePeriods.length * 2">
                <v-progress-linear indeterminate color="primary" />
              </td>
            </tr>
            <tr v-else-if="snapshotScoreRows.length === 0">
              <td :colspan="1 + snapshotScorePeriods.length * 2" class="text-medium-emphasis">
                Keine Scores geladen.
              </td>
            </tr>
            <template v-else>
              <tr v-for="row in snapshotScoreRows" :key="row.key">
                <td>
                  <strong>{{ row.label }}</strong>
                  <div class="text-caption text-medium-emphasis">
                    {{ formatSnapshotScoreRange(row) }}
                  </div>
                </td>
                <template v-for="period in snapshotScorePeriods" :key="`${row.key}-${period.value}`">
                  <td class="text-right">
                    <div
                      v-if="getSnapshotScoreEntry(row, period.value, 'gain')"
                      class="portfolio-score-cell text-success"
                    >
                      <strong>{{ formatSnapshotScoreValue(getSnapshotScoreEntry(row, period.value, "gain"), "gain") }}</strong>
                      <span>{{ formatSnapshotScorePeriod(getSnapshotScoreEntry(row, period.value, "gain")) }}</span>
                    </div>
                    <span v-else class="text-medium-emphasis">-</span>
                  </td>
                  <td class="text-right">
                    <div
                      v-if="getSnapshotScoreEntry(row, period.value, 'loss')"
                      class="portfolio-score-cell text-error"
                    >
                      <strong>{{ formatSnapshotScoreValue(getSnapshotScoreEntry(row, period.value, "loss"), "loss") }}</strong>
                      <span>{{ formatSnapshotScorePeriod(getSnapshotScoreEntry(row, period.value, "loss")) }}</span>
                    </div>
                    <span v-else class="text-medium-emphasis">-</span>
                  </td>
                </template>
              </tr>
            </template>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>

    <!-- ===================== -->
<!-- NEU: Trading-Disziplin Analyse (unterhalb Jahresübersicht) -->
    <!-- ===================== -->
    <v-card class="mt-6" variant="outlined">
      <v-card-title class="d-flex align-center justify-space-between">
        <div>
          <div class="text-h6">Trading-Disziplin Analyse</div>
          <div class="text-caption">
            Quelle: <code>/analytics/trading-discipline</code>
          </div>
        </div>

        <div class="text-caption">
          Zeitraum: <strong>{{ tdFrom }}</strong> -> <strong>{{ tdTo }}</strong>
        </div>
      </v-card-title>

      <v-card-text>
        <v-row class="mb-2" align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="tdBasis"
              :items="['count','volume']"
              label="Basis"
              density="compact"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="tdQuickExitDays"
              type="number"
              min="1"
              max="365"
              label="Quick Exit (Tage)"
              density="compact"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="tdReentryDays"
              type="number"
              min="1"
              max="365"
              label="Re-Entry (Tage)"
              density="compact"
            />
          </v-col>

          <v-col cols="12" md="3" class="text-right">
            <v-btn
              color="primary"
              variant="tonal"
              :loading="tdLoading"
              @click="loadTradingDiscipline"
            >
              Analyse laden
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-text-field
              v-model="tdFrom"
              type="date"
              label="Von"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="tdTo"
              type="date"
              label="Bis"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-center justify-end">
            <v-chip
              v-if="tdAmpel"
              class="ml-2"
              :color="tdAmpel.color"
              variant="flat"
              size="small"
            >
              Konzentration: {{ tdAmpel.label }}
            </v-chip>
            <v-chip
              v-if="tdInterpretationShort"
              class="ml-2"
              variant="tonal"
              size="small"
            >
              {{ tdInterpretationShort }}
            </v-chip>
          </v-col>
        </v-row>

        <v-alert v-if="tdError" type="error" variant="tonal" class="mb-4">
          {{ tdError }}
        </v-alert>

        <template v-if="tdData">
          <!-- Intensity KPIs -->
          <v-row class="mb-4">
            <v-col cols="12" md="3">
              <v-card variant="tonal" class="pa-4">
                <div class="text-caption">Trades gesamt</div>
                <div class="text-h5">
                  {{ tdData?.intensity?.trades_total ?? 0 }}
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="3">
              <v-card variant="tonal" class="pa-4">
                <div class="text-caption">Käufe / Verkäufe</div>
                <div class="text-h5">
                  {{ tdData?.intensity?.buys ?? 0 }} / {{ tdData?.intensity?.sells ?? 0 }}
                </div>
                <div class="text-caption">
                  {{ tdData?.intensity?.buy_pct ?? 0 }}% / {{ tdData?.intensity?.sell_pct ?? 0 }}%
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="3">
              <v-card variant="tonal" class="pa-4">
                <div class="text-caption">Trades pro Monat</div>
                <div class="text-h5">
                  {{ tdData?.intensity?.trades_per_month ?? 0 }}
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="3">
              <v-card variant="tonal" class="pa-4">
                <div class="text-caption">Aktive Tage</div>
                <div class="text-h5">
                  {{ tdData?.intensity?.active_days ?? 0 }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Konzentrations-Check -->
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="d-flex align-center justify-space-between">
              <div class="text-h6">Konzentrations-Check</div>
              <div class="text-caption">
                Ignoriert: <code>stocks_name</code> beginnt mit <code>0_</code>
              </div>
            </v-card-title>

            <v-card-text>
              <v-row class="mb-2">
                <v-col cols="12" md="3">
                  <div class="text-caption">Top-1</div>
                  <div class="text-h6">
                    <span v-if="tdConcentration.top1?.name">
                      {{ tdConcentration.top1.name }}
                      <span class="text-caption">({{ tdConcentration.top1.pct }}%)</span>
                    </span>
                    <span v-else>-</span>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="text-caption">Top-3 Anteil</div>
                  <div class="text-h6">{{ tdConcentration.top3_pct }}%</div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="text-caption">Top-5 Anteil</div>
                  <div class="text-h6">{{ tdConcentration.top5_pct }}%</div>
                </v-col>

                <v-col cols="12" md="3">
                  <div class="text-caption">Top-10 Anteil</div>
                  <div class="text-h6">{{ tdConcentration.top10_pct }}%</div>
                </v-col>
              </v-row>

            <v-data-table
              :headers="tdRankHeaders"
              :items="tdConcentration.ranking"
              :sort-by="[{ key: 'pct', order: 'desc' }]"
              density="compact"
              :items-per-page="-1"
            >
              <template #item.pct="{ item }">
                {{ item.pct }}%
              </template>
            </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Whipsaw Flags -->
          <v-card variant="outlined">
            <v-card-title class="d-flex align-center justify-space-between">
              <div class="text-h6">Whipsaw / Re-Entry Flags</div>
              <div class="text-caption">
                QuickExit ≤ {{ tdQuickExitDays }} Tage, Re-Entry ≤ {{ tdReentryDays }} Tage
              </div>
            </v-card-title>

            <v-card-text>
              <v-alert
                v-if="(tdFlags?.length || 0) === 0"
                type="info"
                variant="tonal"
                class="mb-3"
              >
                Keine auffälligen Titel im gewählten Zeitraum (nach Filter).
              </v-alert>

            <v-data-table
              v-else
              :headers="tdWhipsawHeaders"
              :items="tdFlags"
              :sort-by="[{ key: 'total_trades', order: 'desc' }]"
              density="compact"
              :items-per-page="-1"
            >
            <template #item.quick_exit_rate_pct="{ item }">
              <v-chip
                :color="item.quick_exit_rate_pct > 50 ? 'error' : (item.quick_exit_rate_pct > 25 ? 'warning' : 'success')"
                size="small"
                variant="flat"
              >
                {{ item.quick_exit_rate_pct }}%
              </v-chip>
            </template>

            <template #item.reentry_rate_pct="{ item }">
              <span :class="item.reentry_rate_pct > 50 ? 'text-error font-weight-bold' : ''">
                {{ item.reentry_rate_pct }}%
              </span>
            </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Kurze Interpretation (automatisch) -->
          <v-card variant="tonal" class="mt-4">
            <v-card-title class="text-h6">Kurz-Interpretation</v-card-title>
            <v-card-text>
              <ul style="margin: 0; padding-left: 1.2rem;">
                <li v-for="(line, idx) in tdInterpretationLines" :key="idx">
                  {{ line }}
                </li>
              </ul>
            </v-card-text>
          </v-card>
        </template>
      </v-card-text>
    </v-card>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { useDisplay } from "vuetify";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const { mdAndUp } = useDisplay();
const store = useStore();

// Frontend-Mapping: user_id -> username
const userNameById = {
  1: "joe",
  2: "steve",
  3: "elli",
  4: "hanni",
  5: "ufo",
  6: "rainer",
  7: "sandra",
};

const stockTypeOptions = [
  "Aktie",
  "ETF",
  "Krypto",
  "Rohstoff",
  "Fonds",
  "Cash",
  "Aggregation"
];

const selectedTypes = ref([...stockTypeOptions]);


// --- Admin detection (anpassen, falls du User woanders speicherst) ---
const authUser = computed(() => store.getters.stateUser || null);
const isAdmin = computed(() => Number(authUser.value?.iduser || 0) === 1);


// --- Admin: verfügbare User laden ---
const availableUsers = ref([]); // [{user_id: 1}, ...]
const selectedUserIds = ref([]); // [1,2,3]

function displayUserName(u) {
  const id = Number(u?.user_id);
  return u?.user_username || u?.username || userNameById[id] || `User ${id}`;
}

const defaultUserIds = [1, 7, 5];
const defaultUserGroupLabel = computed(() => defaultUserIds
  .map((id) => displayUserName(availableUsers.value.find((u) => Number(u.user_id) === id) || { user_id: id }))
  .join(" / "));

const userOptions = computed(() =>
  availableUsers.value.map((u) => ({
    title: displayUserName(u),
    value: Number(u.user_id),
  }))
);

function selectAllUsers() {
  selectedUserIds.value = availableUsers.value.map((u) => Number(u.user_id));
}

async function selectDefaultUsers() {
  if (!availableUsers.value.length) {
    await loadAvailableUsers();
  }

  const availableIds = new Set(availableUsers.value.map((u) => Number(u.user_id)));

  selectedUserIds.value = defaultUserIds
    .filter((id) => availableIds.has(id));
}

function clearUsers() {
  selectedUserIds.value = [];
}

function sumByType(arr, typ) {
  const a = Array.isArray(arr) ? arr : [];
  return a
    .filter((p) => getTyp(p) === typ)
    .reduce((acc, p) => acc + Number(p?.wert || 0), 0);
}

async function loadAvailableUsers() {
  if (!isAdmin.value) return;
  try {
    const data = await store.dispatch("getAvailablePortfolioUsersAtDate", selectedDate.value);
    availableUsers.value = Array.isArray(data?.users) ? data.users : [];
    // default: User 1
        if (selectedUserIds.value.length === 0) {
          // Default: nur User 1
          const hasUser1 = availableUsers.value.some(u => Number(u.user_id) === 1);
          selectedUserIds.value = hasUser1 ? [1] : [];
        }
  } catch (e) {
    console.error("loadAvailableUsers failed", e);
  }
}



/** =========================
 * Portfolio Jahresübersicht
 * ========================= */
const portfolioTimelineLoading = ref(false);
const loading = computed(() => portfolioTimelineLoading.value || store.getters.statePortfolioLoading);
const storeError = computed(() => store.getters.statePortfolioError);
const portfolioTimelineError = ref(null);
const error = computed(() => (
  portfolioTimelineError.value || (storeError.value ? String(storeError.value) : null)
));

const periodMode = ref("daily");
const periodModeOptions = [
  { title: "Täglich", value: "daily" },
  { title: "Monatlich", value: "monthly" },
  { title: "Jährlich", value: "yearly" },
];
const showDaily = computed(() => periodMode.value === "daily");
const showMonthly = computed(() => periodMode.value === "monthly");
const periodUnitLabel = computed(() => {
  if (showDaily.value) return "Tag";
  if (showMonthly.value) return "Monat";
  return "Jahr";
});
const showProjection = ref(false);
const projectionPeriods = ref(30);
const maxProjectionPeriods = 200;
const projectionPeriodOptions = [
  10,
  20,
  30,
  40,
  50,
  100,
  150,
  200,
  { title: "Max (alle)", value: "max" },
];
const resolvedProjectionPeriods = computed(() =>
  projectionPeriods.value === "max"
    ? maxProjectionPeriods
    : Number(projectionPeriods.value || 30)
);
const projectionTargetValue = ref("");
const showPositions = ref(false);
const comparisonPeriodLabel = computed(() =>
  showDaily.value ? "Vortag" : showMonthly.value ? "Vormonat" : "Vorjahr"
);

const now = new Date();
const currentYear = now.getFullYear();
const currentDateIso = [
  now.getFullYear(),
  String(now.getMonth() + 1).padStart(2, "0"),
  String(now.getDate()).padStart(2, "0"),
].join("-");

function addDaysIso(dateIso, days) {
  const d = new Date(`${dateIso}T00:00:00`);
  d.setDate(d.getDate() + Number(days || 0));
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function minIsoDate(a, b) {
  return String(a || "") <= String(b || "") ? a : b;
}

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

const fromYear = ref(currentYear - 5);
const toYear = ref(currentYear);
const dailyTo = ref(currentDateIso);
const dailyFrom = ref(addDaysIso(currentDateIso, -19));
const snapshotScoreValueMode = ref("total");
const snapshotScoreValueModeOptions = [
  { title: "Gesamt", value: "total" },
  { title: "Realisiert", value: "realized" },
  { title: "Virtuell", value: "virtual" },
];
const snapshotScoreBasis = ref("eur");
const snapshotScoreBasisOptions = [
  { title: "EUR", value: "eur" },
  { title: "Prozent", value: "pct" },
];
const snapshotScorePeriodOptions = [
  { title: "Tag", value: "day" },
  { title: "Monat", value: "month" },
  { title: "Jahr", value: "year" },
];
const snapshotScorePeriods = computed(() => {
  if (showDaily.value) return snapshotScorePeriodOptions.filter((period) => period.value === "day");
  if (showMonthly.value) return snapshotScorePeriodOptions.filter((period) => period.value === "month");
  return snapshotScorePeriodOptions.filter((period) => period.value === "year");
});
const snapshotScores = ref([]);
const snapshotScoresLoading = ref(false);
const snapshotScoresError = ref(null);
let snapshotScoresRequestId = 0;
let snapshotScoresLastSignature = "";
let snapshotScoresInFlightSignature = "";
let initializingPortfolioDashboard = false;

const rows = ref([]);
const expandedRows = ref([]);
const positionsByPeriod = ref({});
const portfolioTimelineCanvas = ref(null);
let portfolioTimelineChart = null;
let portfolioTimelineRenderToken = 0;
let portfolioLoadRequestId = 0;
let portfolioLoadLastSignature = "";
let portfolioLoadInFlightSignature = "";
const portfolioDataCache = new Map();
const portfolioDataInFlight = new Map();

function isCountablePeriodDelta(value) {
  const delta = Number(value);
  return Number.isFinite(delta) && delta !== 0;
}

const averageDeltaRows = computed(() =>
  rows.value.filter((row) => isCountablePeriodDelta(row?.diff))
);

const portfolioTimelineMaximum = computed(() =>
  rows.value.reduce((maximum, row) => {
    if (!row?.stichtag || row.wert_gesamt == null || !Number.isFinite(Number(row.wert_gesamt))) return maximum;
    if (!maximum || Number(row.wert_gesamt) > Number(maximum.wert_gesamt)
      || (Number(row.wert_gesamt) === Number(maximum.wert_gesamt)
        && String(row.stichtag) > String(maximum.stichtag))) return row;
    return maximum;
  }, null)
);

const averagePeriodDelta = computed(() => {
  if (!averageDeltaRows.value.length) return null;

  const sum = averageDeltaRows.value.reduce(
    (acc, row) => acc + Number(row?.diff || 0),
    0
  );

  return sum / averageDeltaRows.value.length;
});

const averagePeriodDeltaPct = computed(() => {
  const percentageRows = averageDeltaRows.value.filter(
    (row) => row.yoy != null && Number.isFinite(Number(row.yoy))
  );
  if (!percentageRows.length) return null;

  return percentageRows.reduce((sum, row) => sum + Number(row.yoy), 0) / percentageRows.length;
});

const averageDeltaCount = computed(() => averageDeltaRows.value.length);

function parseProjectionTarget(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  let normalized = raw.replace(/[^\d,.-]/g, "");
  if (!normalized) return null;

  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else {
    const parts = normalized.split(".");
    const hasThousandDots =
      parts.length > 1 &&
      parts.slice(1).every((part) => part.length === 3) &&
      parts.every((part) => /^\d+$/.test(part));

    if (hasThousandDots) {
      normalized = parts.join("");
    }
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

const projectionSummary = computed(() => {
  const timelineRows = [...rows.value]
    .filter((row) => row?.stichtag && Number.isFinite(Number(row?.wert_gesamt)))
    .sort((a, b) => String(a.stichtag).localeCompare(String(b.stichtag)));

  if (timelineRows.length < 2) return null;

  const values = timelineRows.map((row) => Number(row.wert_gesamt));
  const deltas = [];
  for (let i = 1; i < values.length; i++) {
    const delta = values[i] - values[i - 1];
    if (isCountablePeriodDelta(delta)) deltas.push(delta);
  }

  if (!deltas.length) return null;

  const avgDelta = deltas.reduce((acc, delta) => acc + delta, 0) / deltas.length;
  const periods = resolvedProjectionPeriods.value;
  const lastValue = values[values.length - 1];
  const totalValue = lastValue + avgDelta * periods;
  const targetValue = parseProjectionTarget(projectionTargetValue.value);
  let targetText = "";

  if (targetValue !== null) {
    if (lastValue > targetValue) {
      targetText = `Zielwert ${formatEUR(targetValue)} ist bereits überschritten.`;
    } else if (avgDelta <= 0) {
      targetText = `Zielwert ${formatEUR(targetValue)} wird bei dieser Hochrechnung nicht überschritten.`;
    } else {
      const targetPeriods = Math.floor((targetValue - lastValue) / avgDelta) + 1;
      const targetLabel = showDaily.value
        ? targetPeriods === 1 ? "Tag" : "Tagen"
        : showMonthly.value
          ? targetPeriods === 1 ? "Monat" : "Monaten"
          : targetPeriods === 1 ? "Jahr" : "Jahren";

      targetText = `Zielwert ${formatEUR(targetValue)} wird nach ${targetPeriods} ${targetLabel} überschritten.`;
    }
  }

  return {
    periods,
    periodLabel: showDaily.value ? "Tagen" : showMonthly.value ? "Monaten" : "Jahren",
    totalValue,
    targetText,
  };
});

const yearOptions = computed(() => {
  const arr = [];
  for (let y = currentYear; y >= 2000; y--) arr.push(y);
  return arr;
});

const snapshotScoreFrom = computed(() =>
  showDaily.value ? dailyFrom.value : `${fromYear.value}-01-01`
);
const snapshotScoreTo = computed(() => {
  if (showDaily.value) return minIsoDate(dailyTo.value, currentDateIso);
  return Number(toYear.value) === currentYear ? currentDateIso : `${toYear.value}-12-31`;
});
const snapshotScoreRows = computed(() => snapshotScores.value);

const headers = computed(() => {
  const comparisonLabel = comparisonPeriodLabel.value;
  const base = [
    { title: showDaily.value ? "Tag" : showMonthly.value ? "Monat" : "Jahr", key: "periodLabel" },
    { title: "Stichtag", key: "stichtag" },
    { title: "Positionen", key: "pos_count" },
    { title: "Wert gesamt", key: "wert_gesamt" },
    { title: `Delta ${comparisonLabel}`, key: "diff" },
    { title: showDaily.value ? "DoD %" : showMonthly.value ? "MoM %" : "YoY %", key: "yoy" },
  ];

  if (mdAndUp.value) {
    base.push(
      { title: `Delta ${comparisonLabel} Aktie`, key: "diff_aktie" },
      { title: `Delta ${comparisonLabel} ETF`, key: "diff_etf" },
      { title: `Delta ${comparisonLabel} Rohstoff`, key: "diff_rohstoff" },
      { title: `Delta ${comparisonLabel} Cash`, key: "diff_cash" },
    );
  }

  if (showPositions.value) {
    base.push({ title: "", key: "data-table-expand" });
  }

  return base;
});

const posHeaders = [
  { title: "ID", key: "stocks_idstocks" },
  { title: "Name", key: "stocks_name" },
  { title: "Menge", key: "menge" },
  { title: "Kurs", key: "kurs" },
  { title: "Wert", key: "wert" },
  { title: "Quelle", key: "quelle_bestand" },
];

function buildRequestedDates() {
  const dates = [];

  if (showDaily.value) {
    const cappedTo = minIsoDate(dailyTo.value, currentDateIso);
    const start = new Date(`${dailyFrom.value}T00:00:00`);
    const end = new Date(`${cappedTo}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return dates;

    const current = start <= end ? start : end;
    const last = start <= end ? end : start;

    while (current <= last) {
      if (!isWeekend(current)) {
        const stichtag = toISODate(current);
        dates.push({
          periodKey: stichtag,
          periodLabel: formatDateDE(stichtag),
          stichtag,
        });
      }
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  if (!showMonthly.value) {
    for (let y = fromYear.value; y <= toYear.value; y++) {
      dates.push({
        periodKey: `${y}`,
        periodLabel: `${y}`,
        stichtag: y === currentYear ? currentDateIso : `${y}-12-31`,
      });
    }
    return dates;
  }

  for (let y = fromYear.value; y <= toYear.value; y++) {
    const lastMonth = y === currentYear ? now.getMonth() : 11;
    for (let month = 0; month <= lastMonth; month++) {
      const isCurrentMonth = y === currentYear && month === now.getMonth();
      const endOfMonth = new Date(y, month + 1, 0);
      const mm = String(month + 1).padStart(2, "0");
      dates.push({
        periodKey: `${y}-${mm}`,
        periodLabel: `${mm}/${String(y).slice(2, 4)}`,
        stichtag: isCurrentMonth
          ? currentDateIso
          : `${y}-${mm}-${String(endOfMonth.getDate()).padStart(2, "0")}`,
      });
    }
  }

  return dates;
}

function portfolioLoadSignature() {
  return JSON.stringify({
    periodMode: periodMode.value,
    fromYear: fromYear.value,
    toYear: toYear.value,
    dailyFrom: dailyFrom.value,
    dailyTo: dailyTo.value,
    user_ids: selectedPortfolioUserIds(),
    selectedTypes: selectedTypes.value,
  });
}

function portfolioDataSignature(stichtag, userIds = selectedPortfolioUserIds()) {
  return JSON.stringify({
    stichtag,
    user_ids: Array.isArray(userIds) ? [...userIds].map(Number).sort((a, b) => a - b) : null,
  });
}

async function getPortfolioAtDateCached(stichtag, { force = false, commitToStore = false } = {}) {
  const userIds = selectedPortfolioUserIds();
  const signature = portfolioDataSignature(stichtag, userIds);

  if (!force && portfolioDataCache.has(signature)) {
    return portfolioDataCache.get(signature);
  }

  if (!force && portfolioDataInFlight.has(signature)) {
    return portfolioDataInFlight.get(signature);
  }

  let request;
  request = store.dispatch("getPortfolioAtDate", {
    stichtag,
    user_ids: userIds,
    noCache: !commitToStore,
    silent: !commitToStore,
  }).then((data) => {
    portfolioDataCache.set(signature, data);
    return data;
  }).finally(() => {
    if (portfolioDataInFlight.get(signature) === request) {
      portfolioDataInFlight.delete(signature);
    }
  });

  portfolioDataInFlight.set(signature, request);
  return request;
}

const loadAll = async (force = false) => {
  const isForced = force === true;
  const signature = portfolioLoadSignature();

  if (
    !isForced &&
    (
      signature === portfolioLoadInFlightSignature ||
      (signature === portfolioLoadLastSignature && rows.value.length)
    )
  ) {
    return;
  }

  const requestId = ++portfolioLoadRequestId;
  portfolioLoadInFlightSignature = signature;
  portfolioTimelineLoading.value = true;
  portfolioTimelineError.value = null;

  try {
    const tmp = [];
    const requestedDates = buildRequestedDates();
    positionsByPeriod.value = {};
    expandedRows.value = [];

    for (const entry of requestedDates) {
      if (requestId !== portfolioLoadRequestId) return;

      const data = await getPortfolioAtDateCached(entry.stichtag, { force: isForced });

      if (requestId !== portfolioLoadRequestId) return;

      const filtered = filterPos(data.positionen);
      const wertFiltered = filtered.reduce(
        (acc, p) => acc + Number(p.wert || 0),
        0
      );

      tmp.push({
        year: Number(String(entry.stichtag).slice(0, 4)),
        periodKey: entry.periodKey,
        periodLabel: entry.periodLabel,
        stichtag: data.stichtag,
        wert_gesamt: wertFiltered,
        pos_count: filtered.length,

        aktie_sum: sumByType(filtered, "Aktie"),
        etf_sum: sumByType(filtered, "ETF"),
        rohstoff_sum: sumByType(filtered, "Rohstoff"),
        cash_sum: sumByType(filtered, "Cash"),

        diff: null,
        yoy: null,
        diff_aktie: null,
        diff_etf: null,
        diff_rohstoff: null,
        diff_cash: null,
      });
    }

    tmp.sort((a, b) => String(a.stichtag).localeCompare(String(b.stichtag)));

    for (let i = 1; i < tmp.length; i++) {
      const prev = tmp[i - 1];
      const cur = tmp[i];

      cur.diff = cur.wert_gesamt - prev.wert_gesamt;
      cur.yoy =
        prev.wert_gesamt === 0 ? null : (cur.diff / prev.wert_gesamt) * 100;

      cur.diff_aktie = cur.aktie_sum - prev.aktie_sum;
      cur.diff_etf = cur.etf_sum - prev.etf_sum;
      cur.diff_rohstoff = cur.rohstoff_sum - prev.rohstoff_sum;
      cur.diff_cash = cur.cash_sum - prev.cash_sum;
    }

    if (requestId !== portfolioLoadRequestId) return;
    rows.value = tmp.sort((a, b) => String(b.stichtag).localeCompare(String(a.stichtag)));
    portfolioLoadLastSignature = signature;
  } catch (e) {
    if (requestId !== portfolioLoadRequestId) return;
    portfolioTimelineError.value = String(e?.message || e);
    console.error("loadAll failed", e);
  } finally {
    if (requestId === portfolioLoadRequestId) {
      portfolioLoadInFlightSignature = "";
      portfolioTimelineLoading.value = false;
    }
  }
};

function formatEUR(v) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(v || 0);
}

function formatDateDE(value) {
  if (!value) return "";
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text;
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function formatEUR0(v) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v || 0));
}

function getTyp(p) {
  const raw = String(p?.stocks_Typ ?? "Aktie").trim();
  const name = String(p?.stocks_name ?? "").trim().toLowerCase();

  if (name.includes("cash")) {
    return "Cash";
  }

  const normalized = raw.toLowerCase();
  const stockTypeByNormalized = stockTypeOptions.reduce((acc, typ) => {
    acc[String(typ).toLowerCase()] = typ;
    return acc;
  }, {});

  return stockTypeByNormalized[normalized] || raw;
}

function filterPos(arr) {
  const a = Array.isArray(arr) ? arr : [];
  return a.filter(p => selectedTypes.value.includes(getTyp(p)));
}

function getRowPositionState(row) {
  if (!row?.periodKey) {
    return { items: [], loading: false, loaded: false, error: null };
  }

  return (
    positionsByPeriod.value[row.periodKey] || {
      items: [],
      loading: false,
      loaded: false,
      error: null,
    }
  );
}

function getRowPositions(row) {
  return getRowPositionState(row).items;
}

async function loadPositionsForRow(row) {
  if (!row?.periodKey || !row?.stichtag) return;

  const current = getRowPositionState(row);
  if (current.loaded || current.loading) return;

  positionsByPeriod.value[row.periodKey] = {
    ...current,
    loading: true,
    error: null,
  };

  try {
    const data = await getPortfolioAtDateCached(row.stichtag);

    positionsByPeriod.value[row.periodKey] = {
      items: filterPos(data?.positionen),
      loading: false,
      loaded: true,
      error: null,
    };
  } catch (e) {
    positionsByPeriod.value[row.periodKey] = {
      items: [],
      loading: false,
      loaded: false,
      error: String(e?.message || e),
    };
  }
}

function formatPct(v) {
  return `${Number(v).toFixed(2)} %`;
}

function selectedPortfolioUserIds() {
  return isAdmin.value && selectedUserIds.value.length ? selectedUserIds.value : null;
}

function shouldRefreshSnapshotScores() {
  return snapshotScoresLoading.value || snapshotScores.value.length > 0;
}

function getSnapshotScoreRanges() {
  const ranges = [];
  const useHistoryAndFilterRows = !showDaily.value;

  if (useHistoryAndFilterRows) {
    ranges.push({
      key: "all_history",
      label: "Gesamt",
      from: "1900-01-01",
      to: currentDateIso,
      rangeText: "Gesamte Historie",
    });

    ranges.push({
      key: "filter",
      label: "Aktueller Filter",
      from: snapshotScoreFrom.value,
      to: snapshotScoreTo.value,
    });
  } else {
    ranges.push({
      key: "all",
      label: "Gesamt",
      from: snapshotScoreFrom.value,
      to: snapshotScoreTo.value,
    });
  }

  if (useHistoryAndFilterRows) {
    return ranges;
  }

  for (let year = Number(fromYear.value); year <= Number(toYear.value); year++) {
    const from = `${year}-01-01`;
    const to = year === currentYear ? currentDateIso : `${year}-12-31`;
    const rangeFrom = from < snapshotScoreFrom.value ? snapshotScoreFrom.value : from;
    const rangeTo = to > snapshotScoreTo.value ? snapshotScoreTo.value : to;
    if (rangeFrom <= rangeTo) {
      ranges.push({
        key: String(year),
        label: String(year),
        from: rangeFrom,
        to: rangeTo,
      });
    }
  }

  return ranges;
}

function formatSnapshotScoreRange(row) {
  if (row?.rangeText) return row.rangeText;
  const from = row?.from ? formatDateDE(row.from) : "";
  const to = row?.to ? formatDateDE(row.to) : "";
  return from && to ? `${from} - ${to}` : from || to || "";
}

function firstSnapshotScoreRow(data) {
  const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
  return rows[0] || null;
}

async function loadSnapshotScoreEntry(range, period, kind) {
  const data = await store.dispatch("getPortfolioSnapshotScores", {
    kind,
    period,
    basis: snapshotScoreBasis.value,
    from: range.from,
    to: range.to,
    limit: 1,
    user_ids: selectedPortfolioUserIds(),
  });
  return firstSnapshotScoreRow(data);
}

async function loadSnapshotScores(force = false) {
  const ranges = getSnapshotScoreRanges();
  const periods = snapshotScorePeriods.value;
  const userIds = selectedPortfolioUserIds();
  const signature = JSON.stringify({
    basis: snapshotScoreBasis.value,
    periods: periods.map((period) => period.value),
    ranges: ranges.map((range) => [range.key, range.from, range.to]),
    user_ids: userIds,
  });

  if (
    !force &&
    (
      signature === snapshotScoresInFlightSignature ||
      (signature === snapshotScoresLastSignature && snapshotScores.value.length)
    )
  ) {
    return;
  }

  const requestId = ++snapshotScoresRequestId;
  snapshotScoresInFlightSignature = signature;
  snapshotScoresError.value = null;
  snapshotScoresLoading.value = true;

  try {
    const loadedRows = await Promise.all(
      ranges.map(async (range) => {
        const scores = {};

        await Promise.all(
          periods.flatMap((period) => [
            loadSnapshotScoreEntry(range, period.value, "gain").then((entry) => {
              scores[`${period.value}_gain`] = entry;
            }),
            loadSnapshotScoreEntry(range, period.value, "loss").then((entry) => {
              scores[`${period.value}_loss`] = entry;
            }),
          ])
        );

        return {
          ...range,
          scores,
        };
      })
    );

    if (requestId !== snapshotScoresRequestId) return;
    snapshotScores.value = loadedRows;
    snapshotScoresLastSignature = signature;
  } catch (e) {
    if (requestId !== snapshotScoresRequestId) return;
    snapshotScoresError.value = String(e?.message || e);
    snapshotScores.value = [];
  } finally {
    if (requestId === snapshotScoresRequestId) {
      snapshotScoresInFlightSignature = "";
      snapshotScoresLoading.value = false;
    }
  }
}

function getSnapshotScoreEntry(row, period, kind) {
  return row?.scores?.[`${period}_${kind}`] || null;
}

function firstFiniteSnapshotValue(row, keys) {
  for (const key of keys) {
    const value = Number(row?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

const snapshotScoreValueFieldsByMode = {
  total: {
    eur: ["gain_eur", "delta_eur", "diff_eur", "change_eur"],
    lossEur: ["loss_eur", "gain_eur", "delta_eur", "diff_eur", "change_eur"],
    pct: ["gain_pct", "delta_pct", "diff_pct", "change_pct"],
    lossPct: ["loss_pct", "gain_pct", "delta_pct", "diff_pct", "change_pct"],
    start: ["start_value"],
    end: ["end_value"],
  },
  realized: {
    eur: ["gain_realized_eur"],
    lossEur: ["loss_realized_eur", "gain_realized_eur"],
    pct: ["gain_realized_pct"],
    lossPct: ["loss_realized_pct", "gain_realized_pct"],
    start: ["start_value_realized"],
    end: ["end_value_realized"],
  },
  virtual: {
    eur: ["gain_virtual_eur"],
    lossEur: ["loss_virtual_eur", "gain_virtual_eur"],
    pct: ["gain_virtual_pct"],
    lossPct: ["loss_virtual_pct", "gain_virtual_pct"],
    start: ["start_value_virtual"],
    end: ["end_value_virtual"],
  },
};

function calculateSnapshotPct(row, fields) {
  const startValue = firstFiniteSnapshotValue(row, fields.start);
  const endValue = firstFiniteSnapshotValue(row, fields.end);
  if (!Number.isFinite(startValue) || startValue === 0 || !Number.isFinite(endValue)) {
    return null;
  }

  return ((endValue - startValue) / startValue) * 100;
}

function formatSnapshotScoreValue(row, kind) {
  if (!row) return "-";
  const fields = snapshotScoreValueFieldsByMode[snapshotScoreValueMode.value] ||
    snapshotScoreValueFieldsByMode.total;
  const eurValue = firstFiniteSnapshotValue(row, kind === "gain" ? fields.eur : fields.lossEur);
  const pctValue = firstFiniteSnapshotValue(row, kind === "gain" ? fields.pct : fields.lossPct) ??
    calculateSnapshotPct(row, fields);
  const value = snapshotScoreBasis.value === "pct" ? pctValue : eurValue;
  if (value === null) return "-";

  const sign = kind === "loss" && value > 0 ? -1 : 1;
  const signedValue = value * sign;
  return snapshotScoreBasis.value === "pct"
    ? formatPct(signedValue)
    : formatEUR(signedValue);
}

function formatSnapshotScorePeriod(row) {
  if (!row) return "";
  const key = String(row.period_key || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) return formatDateDE(key);
  if (/^\d{4}-\d{2}$/.test(key)) return `${key.slice(5, 7)}/${key.slice(0, 4)}`;
  if (key) return key;

  const start = row.start_date ? formatDateDE(row.start_date) : "";
  const end = row.end_date ? formatDateDE(row.end_date) : "";
  return start && end ? `${start} - ${end}` : start || end;
}

function diffClass(v) {
  if (v > 0) return "text-success";
  if (v < 0) return "text-error";
  return "text-medium-emphasis";
}

function destroyPortfolioTimelineChart() {
  if (!portfolioTimelineChart) return;
  try {
    portfolioTimelineChart.destroy();
  } catch (e) {
    console.warn("destroyPortfolioTimelineChart failed", e);
  } finally {
    portfolioTimelineChart = null;
  }
}

function parseIsoDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (![year, month, day].every(Number.isFinite)) return null;
  return { year, month, day };
}

function buildProjectionLabels(lastStichtag, count = 20) {
  const parsed = parseIsoDate(lastStichtag);
  if (!parsed) return [];

  const labels = [];
  for (let step = 1; step <= count; step++) {
    if (showDaily.value) {
      labels.push(addDaysIso(lastStichtag, step));
    } else if (showMonthly.value) {
      const d = new Date(parsed.year, parsed.month + step, 0);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      labels.push(`${yyyy}-${mm}-${dd}`);
    } else {
      labels.push(`${parsed.year + step}-12-31`);
    }
  }

  return labels;
}

function buildProjectionDataset(timelineRows, projectedLabels) {
  if (!showProjection.value || timelineRows.length < 2 || !projectedLabels.length) {
    return null;
  }

  const values = timelineRows.map((row) => Number(row.wert_gesamt));
  const deltas = [];
  for (let i = 1; i < values.length; i++) {
    const delta = values[i] - values[i - 1];
    if (isCountablePeriodDelta(delta)) deltas.push(delta);
  }

  if (!deltas.length) return null;

  const avgDelta = deltas.reduce((acc, delta) => acc + delta, 0) / deltas.length;
  const lastValue = values[values.length - 1];
  const projectedValues = projectedLabels.map((_, index) => lastValue + avgDelta * (index + 1));

  return {
    label: "Hochrechnung",
    data: [
      ...Array(Math.max(values.length - 1, 0)).fill(null),
      lastValue,
      ...projectedValues,
    ],
    borderColor: "#f97316",
    backgroundColor: "rgba(249, 115, 22, 0.08)",
    fill: false,
    tension: 0.25,
    borderWidth: 2,
    borderDash: [7, 5],
    pointRadius: 0,
    pointHoverRadius: 3,
    spanGaps: true,
  };
}

function buildProjectionTargetDataset(timelineRows, chartLabels) {
  const targetValue = parseProjectionTarget(projectionTargetValue.value);
  if (!showProjection.value || targetValue === null || timelineRows.length < 2) {
    return null;
  }

  const values = timelineRows.map((row) => Number(row.wert_gesamt));
  const deltas = [];
  for (let i = 1; i < values.length; i++) {
    const delta = values[i] - values[i - 1];
    if (isCountablePeriodDelta(delta)) deltas.push(delta);
  }

  if (!deltas.length) return null;

  const avgDelta = deltas.reduce((acc, delta) => acc + delta, 0) / deltas.length;
  const lastValue = values[values.length - 1];
  if (lastValue > targetValue || avgDelta <= 0) return null;

  const targetPeriods = Math.floor((targetValue - lastValue) / avgDelta) + 1;
  const targetIndex = values.length - 1 + targetPeriods;
  if (targetIndex > chartLabels.length - 1) return null;

  const data = Array(chartLabels.length).fill(null);
  data[targetIndex] = lastValue + avgDelta * targetPeriods;

  return {
    label: "Zielwert überschritten",
    data,
    borderColor: "#f97316",
    backgroundColor: "#f97316",
    pointRadius: 5,
    pointHoverRadius: 6,
    showLine: false,
  };
}

function formatPortfolioTimelineAxisValue(value, maximumFractionDigits) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("de-DE", {
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(n);
}

function buildPortfolioTimelineYAxisOptions(datasets) {
  const values = datasets
    .flatMap((dataset) => dataset.data || [])
    .filter((value) => Number.isFinite(Number(value)))
    .map(Number);

  if (!values.length) {
    return {
      ticks: {
        maxTicksLimit: 4,
        font: { size: 10 },
        callback: (value) => formatPortfolioTimelineAxisValue(value, 1),
      },
      grid: {
        color: "rgba(100, 116, 139, 0.15)",
      },
    };
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const magnitude = Math.max(Math.abs(minValue), Math.abs(maxValue), 1);
  const relativeRange = range / magnitude;
  const maximumFractionDigits = relativeRange < 0.03 ? 2 : 1;
  const padding = range > 0 ? Math.max(range * 0.15, magnitude * 0.001) : magnitude * 0.01;
  const stepBase = magnitude >= 1_000_000 ? 1000 : 100;
  const suggestedMin = Math.max(0, Math.floor((minValue - padding) / stepBase) * stepBase);
  const suggestedMax = Math.ceil((maxValue + padding) / stepBase) * stepBase;

  return {
    min: suggestedMin,
    max: suggestedMax > suggestedMin ? suggestedMax : suggestedMin + stepBase,
    ticks: {
      maxTicksLimit: 4,
      font: { size: 10 },
      callback: (value) => formatPortfolioTimelineAxisValue(value, maximumFractionDigits),
    },
    grid: {
      color: "rgba(100, 116, 139, 0.15)",
    },
  };
}

async function renderPortfolioTimelineChart() {
  const renderToken = ++portfolioTimelineRenderToken;
  await nextTick();
  if (renderToken !== portfolioTimelineRenderToken) return;

  const canvas = portfolioTimelineCanvas.value;
  if (!canvas) return;

  const timelineRows = [...rows.value]
    .filter((row) => row?.stichtag && Number.isFinite(Number(row?.wert_gesamt)))
    .sort((a, b) => String(a.stichtag).localeCompare(String(b.stichtag)));

  destroyPortfolioTimelineChart();
  if (renderToken !== portfolioTimelineRenderToken) return;
  if (!timelineRows.length) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  if (renderToken !== portfolioTimelineRenderToken) return;

  const projectionLabels = buildProjectionLabels(
    timelineRows[timelineRows.length - 1]?.stichtag,
    resolvedProjectionPeriods.value
  );
  const chartLabels = showProjection.value
    ? [...timelineRows.map((row) => String(row.stichtag)), ...projectionLabels]
    : timelineRows.map((row) => String(row.stichtag));
  const lastValueIndex = timelineRows.length - 1;
  const lastChartIndex = chartLabels.length - 1;
  const maximumIndex = timelineRows.indexOf(portfolioTimelineMaximum.value);
  const isMaximumPoint = (context) => context.dataIndex === maximumIndex;
  const valueDataset = {
    label: "Wert gesamt",
    data: [
      ...timelineRows.map((row) => Number(row.wert_gesamt)),
      ...Array(showProjection.value ? projectionLabels.length : 0).fill(null),
    ],
    borderColor: "#1d4ed8",
    backgroundColor: "rgba(29, 78, 216, 0.12)",
    fill: true,
    tension: 0.25,
    borderWidth: 2,
    pointRadius: (context) => isMaximumPoint(context) ? 6 : 2,
    pointHoverRadius: (context) => isMaximumPoint(context) ? 8 : 3,
    pointBackgroundColor: (context) => isMaximumPoint(context) ? "#fbbf24" : "#1d4ed8",
    pointBorderColor: (context) => isMaximumPoint(context) ? "#ffffff" : "#1d4ed8",
    pointBorderWidth: (context) => isMaximumPoint(context) ? 2 : 1,
  };
  const projectionDataset = buildProjectionDataset(timelineRows, projectionLabels);
  const projectionTargetDataset = buildProjectionTargetDataset(timelineRows, chartLabels);
  const datasets = [valueDataset];
  if (projectionDataset) datasets.push(projectionDataset);
  if (projectionTargetDataset) datasets.push(projectionTargetDataset);
  const yAxisOptions = buildPortfolioTimelineYAxisOptions(datasets);

  portfolioTimelineChart = new Chart(ctx, {
    type: "line",
    data: {
      labels: chartLabels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: showProjection.value,
          labels: {
            boxWidth: 12,
            font: {
              size: 10,
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${formatEUR(ctx.parsed?.y || 0)}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            autoSkip: false,
            minRotation: 0,
            maxRotation: 0,
            font: {
              size: 10,
            },
            callback: (value, index) => {
              const raw = chartLabels[index];
              if (!raw) return "";
              if (index === lastValueIndex || index === lastChartIndex) {
                return formatDateDE(raw);
              }
              const tickStep = Math.max(1, Math.ceil(chartLabels.length / 4));
              if (index % tickStep !== 0) return "";
              const text = String(raw);
              const year = text.slice(2, 4);
              const month = text.slice(5, 7);
              const day = text.slice(8, 10);
              if (showDaily.value) return `${day}.${month}`;
              return showMonthly.value ? `${month}/${year}` : text.slice(0, 4);
            },
          },
          grid: {
            display: false,
          },
        },
        y: yAxisOptions,
      },
    },
  });

  requestAnimationFrame(() => {
    if (renderToken !== portfolioTimelineRenderToken || !portfolioTimelineChart) return;
    portfolioTimelineChart.resize();
    portfolioTimelineChart.update("none");
  });
}

/** =========================
 * Stichtag-Ansicht
 * ========================= */
function toISODate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const selectedDate = ref(toISODate(new Date()));

watch(periodMode, (mode, previousMode) => {
  if (mode === "daily" && previousMode !== "daily") {
    dailyTo.value = currentDateIso;
    dailyFrom.value = addDaysIso(currentDateIso, -19);
  }

  if (mode === "monthly" && previousMode !== "monthly" && fromYear.value !== toYear.value) {
    fromYear.value = toYear.value;
    return;
  }

  if (mode === "yearly" && previousMode !== "yearly" && fromYear.value !== 2018) {
    fromYear.value = 2018;
    return;
  }

  loadAll();
  if (!initializingPortfolioDashboard && shouldRefreshSnapshotScores()) loadSnapshotScores();
});

watch([fromYear, toYear], () => {
  if (showDaily.value) return;
  loadAll();
  if (!initializingPortfolioDashboard && shouldRefreshSnapshotScores()) loadSnapshotScores();
});

watch([dailyFrom, dailyTo], () => {
  if (!showDaily.value) return;
  if (String(dailyTo.value || "") > currentDateIso) {
    dailyTo.value = currentDateIso;
    return;
  }
  loadAll();
  if (!initializingPortfolioDashboard && shouldRefreshSnapshotScores()) loadSnapshotScores();
});

watch(selectedTypes, () => {
  positionsByPeriod.value = {};
  if (showPositions.value) {
    expandAllRows();
  } else {
    expandedRows.value = [];
  }
}, { deep: true });

function expandAllRows() {
  expandedRows.value = rows.value
    .map((row) => row?.periodKey)
    .filter(Boolean);
}

watch(showPositions, (value) => {
  if (value) {
    expandAllRows();
  } else {
    expandedRows.value = [];
  }
});

watch(rows, () => {
  if (showPositions.value) {
    expandAllRows();
  }
  renderPortfolioTimelineChart();
});

watch(showProjection, () => {
  renderPortfolioTimelineChart();
});

watch(projectionPeriods, () => {
  renderPortfolioTimelineChart();
});

watch(projectionTargetValue, () => {
  renderPortfolioTimelineChart();
});

watch(snapshotScoreBasis, () => {
  snapshotScoresLastSignature = "";
  if (shouldRefreshSnapshotScores()) loadSnapshotScores();
});

watch(selectedUserIds, () => {
  if (initializingPortfolioDashboard) return;
  snapshotScoresLastSignature = "";
  loadAll();
  if (shouldRefreshSnapshotScores()) loadSnapshotScores();
}, { deep: true });

watch(expandedRows, (keys) => {
  keys.forEach((periodKey) => {
    const row = rows.value.find((entry) => entry.periodKey === periodKey);
    if (row) loadPositionsForRow(row);
  });
});
/** =========================
 * Trading-Disziplin Analyse
 * ========================= */
const tdBasis = ref("count");
const tdQuickExitDays = ref(7);
const tdReentryDays = ref(10);

// Default Zeitraum: fromYear-01-01 bis toYear-12-31
const tdFrom = ref(`${fromYear.value}-01-01`);
const tdTo = ref(`${toYear.value}-12-31`);

watch([fromYear, toYear], () => {
  // nur updaten, wenn der User nicht bewusst was anderes gesetzt hat:
  tdFrom.value = `${fromYear.value}-01-01`;
  tdTo.value = `${toYear.value}-12-31`;
});

const tdData = ref(null);
const tdLoading = ref(false);
const tdError = ref(null);

async function loadTradingDiscipline() {
  tdError.value = null;
  tdLoading.value = true;
  try {
    tdData.value = await store.dispatch("getTradingDiscipline", {
      from: tdFrom.value,
      to: tdTo.value,
      basis: tdBasis.value,
      quick_exit_days: tdQuickExitDays.value,
      reentry_days: tdReentryDays.value,
    });
  } catch (e) {
    tdError.value = String(e?.message || e);
    tdData.value = null;
  } finally {
    tdLoading.value = false;
  }
}

// Filter: ignoriere stocks_name die mit "0_" beginnen
function isIgnoredName(name) {
  return typeof name === "string" && name.startsWith("0_");
}

// Konzentration neu berechnen nach Filter (Ranking enthält pct)
const tdConcentration = computed(() => {
  const c = tdData.value?.concentration;
  if (!c) {
    return {
      top1: null,
      top3_pct: 0,
      top5_pct: 0,
      top10_pct: 0,
      ranking: [],
    };
  }

  const rawRanking = Array.isArray(c.ranking) ? c.ranking : [];
  const filtered = rawRanking.filter((r) => !isIgnoredName(r?.name));

  const sumPct = filtered.reduce((acc, r) => acc + (Number(r.pct) || 0), 0) || 0;

  function topPct(n) {
    if (sumPct <= 0) return 0;
    const s = filtered.slice(0, n).reduce((acc, r) => acc + (Number(r.pct) || 0), 0);
    // renormalisieren auf 100% innerhalb des gefilterten Universums
    return Number(((s / sumPct) * 100).toFixed(1));
  }

  const top1 = filtered[0]
    ? {
        stock_id: filtered[0].stock_id,
        name: filtered[0].name,
        pct: Number(((Number(filtered[0].pct || 0) / (sumPct || 1)) * 100).toFixed(1)),
      }
    : null;

  return {
    top1,
    top3_pct: topPct(3),
    top5_pct: topPct(5),
    top10_pct: topPct(10),
    ranking: filtered.map((r) => ({
      ...r,
      pct: sumPct > 0 ? Number(((Number(r.pct || 0) / sumPct) * 100).toFixed(1)) : 0,
    })),
  };
});

const tdFlags = computed(() => {
  const flags = tdData.value?.whipsaw?.flags;
  const arr = Array.isArray(flags) ? flags : [];

  return arr
    .filter((f) => !isIgnoredName(f?.name))
    .map(f => ({
      ...f,
      // Sicherstellen, dass alles Zahlen sind für die Sortierung
      quick_exit_rate_pct: Number(f.quick_exit_rate_pct || 0),
      reentry_rate_pct: Number(f.reentry_rate_pct || 0)
    }))
    .sort((a, b) => b.quick_exit_rate_pct - a.quick_exit_rate_pct);
});
const tdRankHeaders = [
  { title: "Titel", key: "name" },
  { title: "Anteil", key: "pct", align: "end" },
];

const tdWhipsawHeaders = [
  { title: "Titel", key: "name", sortable: true },
  { title: "Trades (FIFO)", key: "total_trades", align: "end", sortable: true },
  { title: "Quick Exits", key: "quick_exits", align: "end", sortable: true },
  { title: "QE Rate", key: "quick_exit_rate_pct", align: "end", sortable: true }, // Diese Spalte ist unser Ziel
  { title: "Re-Entries", key: "reentries", align: "end", sortable: true },
  { title: "RE Rate", key: "reentry_rate_pct", align: "end", sortable: true },
];

// Ampel für Konzentration (Top-5 als Hauptsignal, Top-3 als Verstärker)
const tdAmpel = computed(() => {
  const c = tdConcentration.value;
  const t3 = Number(c.top3_pct || 0);
  const t5 = Number(c.top5_pct || 0);

  // pragmatische Schwellen (leicht anpassbar)
  // Grün: Top-5 <= 60
  // Gelb: Top-5 <= 75 oder Top-3 <= 55
  // Rot: darüber
  if (t5 <= 60) return { color: "success", label: "grün" };
  if (t5 <= 75 || t3 <= 55) return { color: "warning", label: "gelb" };
  return { color: "error", label: "rot" };
});

// Kurze Interpretation (Chip)
const tdInterpretationShort = computed(() => {
  if (!tdData.value) return "";
  const total = tdData.value?.intensity?.trades_total ?? 0;
  const tpm = tdData.value?.intensity?.trades_per_month ?? 0;
  const flagged = tdFlags.value.length;

  const activity =
    tpm >= 6 ? "sehr aktiv" :
    tpm >= 2 ? "aktiv" :
    "eher ruhig";

  const whipsaw =
    flagged >= 5 ? "Whipsaw auffällig" :
    flagged >= 1 ? "einige Whipsaw-Signale" :
    "keine Whipsaw-Signale";

  return `${activity} (${total} Trades), ${whipsaw}`;
});

// Ausführlichere Kurz-Interpretation (Bullet-Liste)
const tdInterpretationLines = computed(() => {
  if (!tdData.value) return [];

  const i = tdData.value.intensity || {};
  const tpm = Number(i.trades_per_month || 0);
  const buys = Number(i.buys || 0);
  const sells = Number(i.sells || 0);
  const buyPct = Number(i.buy_pct || 0);
  const sellPct = Number(i.sell_pct || 0);

  const c = tdConcentration.value;
  const top1 = c.top1?.pct ?? 0;
  const top5 = c.top5_pct ?? 0;

  const flagsN = tdFlags.value.length;

  const lines = [];

  // Intensität
  if (tpm >= 6) {
    lines.push(`Hohe Trading-Intensität (${tpm} Trades/Monat): Timing- und Kostenrisiko steigt typischerweise.`);
  } else if (tpm >= 2) {
    lines.push(`Moderate Trading-Intensität (${tpm} Trades/Monat): aktiv, aber noch im Rahmen.`);
  } else {
    lines.push(`Geringe Trading-Intensität (${tpm} Trades/Monat): eher Buy-&-Hold-lastig.`);
  }

  // Buy/Sell Mix
  lines.push(`Käufe/Verkäufe: ${buys}/${sells} (${buyPct}% / ${sellPct}%).`);

  // Konzentration
  const ampel = tdAmpel.value?.label || "-";
  lines.push(
    `Konzentration (ohne 0_*): Top-1 ${top1}%, Top-5 ${top5}% -> Ampel ${ampel}.`
  );

  // Whipsaw
  if (flagsN >= 5) {
    lines.push(`Whipsaw/ Re-Entry: Viele Auffälligkeiten (${flagsN} Titel) - Stop-Logik & Re-Entry prüfen.`);
  } else if (flagsN >= 1) {
    lines.push(`Whipsaw/ Re-Entry: ${flagsN} auffällige Titel - ggf. Stops zu eng oder häufiges Re-Entry.`);
  } else {
    lines.push(`Whipsaw/ Re-Entry: Keine auffälligen Titel im Zeitraum (nach Filter).`);
  }

  return lines;
});

onMounted(async () => {
  initializingPortfolioDashboard = true;
  try {
    if (isAdmin.value) {
      await loadAvailableUsers();
    }
  } finally {
    initializingPortfolioDashboard = false;
  }

  loadAll();
  loadSnapshotScores();
  loadTradingDiscipline(); // initial direkt mit laden
});

onBeforeUnmount(() => {
  destroyPortfolioTimelineChart();
});
</script>

<style scoped>
.portfolio-mini-chart-card {
  height: 100%;
  min-height: 132px;
  padding: 10px 12px;
}

.portfolio-title-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.portfolio-title-row h2 {
  margin: 0;
}

.portfolio-title-switch {
  flex: 0 0 auto;
}

.portfolio-stat-card {
  height: 100%;
  min-height: 132px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.portfolio-mini-chart-wrap {
  position: relative;
  width: 100%;
  height: 96px;
}

.portfolio-projection-text {
  color: #f97316;
  font-weight: 600;
}

.portfolio-filter-row {
  align-items: center;
}

.portfolio-switches {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.portfolio-switch-control {
  flex: 0 0 auto;
  min-width: 150px;
}

.portfolio-period-select {
  width: 116px;
}

.portfolio-period-mode {
  flex: 0 0 auto;
}

.portfolio-maximum-label {
  color: #fbbf24;
  font-weight: 600;
}

/* Vuetify renders these classes inside the radio components. */
/*noinspection CssUnusedSymbol*/
.portfolio-period-mode :deep(.v-selection-control-group) {
  flex-wrap: nowrap;
  gap: 8px;
}

/*noinspection CssUnusedSymbol*/
.portfolio-period-mode :deep(.v-selection-control) {
  --v-selection-control-size: 28px;
  margin-inline-end: 0;
}

/*noinspection CssUnusedSymbol*/
.portfolio-period-mode :deep(.v-label) {
  font-size: 12px;
}

/*noinspection CssUnusedSymbol*/
.portfolio-period-mode :deep(.v-icon) {
  font-size: 18px;
}

.portfolio-target-input {
  width: 172px;
}

.portfolio-user-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.portfolio-score-title {
  gap: 16px;
  flex-wrap: wrap;
  white-space: normal;
}

.portfolio-score-heading {
  min-width: 0;
}

.portfolio-score-heading code {
  white-space: normal;
  word-break: break-word;
}

.portfolio-score-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.portfolio-score-load-btn {
  flex: 0 0 auto;
}

.portfolio-score-basis {
  width: 132px;
}

.portfolio-score-value-mode {
  width: 156px;
}

.portfolio-score-table {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  overflow-x: auto;
}

.portfolio-score-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: nowrap;
}

.portfolio-score-cell span {
  font-size: 0.75rem;
}

@media (max-width: 600px) {
  .portfolio-score-title {
    align-items: stretch !important;
    flex-direction: column;
  }

  .portfolio-score-actions {
    align-items: stretch;
    justify-content: flex-start;
    width: 100%;
  }

  .portfolio-score-basis,
  .portfolio-score-value-mode,
  .portfolio-score-load-btn {
    width: 100%;
  }
}

.portfolio-sticky-table {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  overflow: hidden;
}

/*noinspection CssUnusedSymbol*/
.portfolio-sticky-table:not(.portfolio-sticky-table--nested) :deep(.v-table__wrapper) {
  max-height: 70vh;
}

.portfolio-sticky-table :deep(thead th) {
  background: #1e1e1e;
  z-index: 2;
}

.portfolio-sticky-table--nested {
  margin-top: 8px;
}
</style>
